import React, { useEffect, useState } from 'react';
import { Logo } from 'components/Logo';

import { SelectMenu } from 'components/SelectMenu';
import Icon from 'components/User/Icon';
import { Link } from 'components/Link/Link';
import { useReactiveVar } from '@apollo/client';

import { getConnectUrl } from 'utils';
import State from 'utils/State';

import { type Option, type NavMenuItem } from 'types';
import { type User } from 'generated/graphql';
import { flush as flushAmplitude } from '@amplitude/analytics-browser';
import { AmplitudeEventCategories, logAmplitudeEvent } from 'utils/Amplitude';
import styles from './Navigation.module.scss';
import { StickyHeader } from '../StickyHeader/StickyHeader';

const { headerCustomTheme } = State;

const VENUE_DROPDOWN_MENU_MAX_HEIGHT = 384; // px; consistent with Connect's equivalent MUI Popper internal max-height value

interface Props {
  menuItems: NavMenuItem[];
  currentUser: User | undefined;
  venueId: string | undefined;
  settings: Option[];
  isLoading?: boolean;
}

export const Navigation: React.FC<Props> = ({ menuItems, currentUser, venueId, settings, isLoading }) => {
  const [hasScrolled, setHasScrolled] = useState(false);

  const _headerCustomTheme = useReactiveVar(headerCustomTheme);

  useEffect(() => {
    window.addEventListener('scroll', () => {
      const { scrollY } = window;

      setHasScrolled(scrollY > 1);
    });
  }, []);

  return (
    <nav className={styles.NavigationOuterContainer}>
      <div
        data-testid="navigation-container"
        className={`${styles.NavigationContainer} ${hasScrolled ? styles.HasScrolled : null} ${
          _headerCustomTheme ? `Theme${_headerCustomTheme}` : null
        }`}
      >
        <div className="container">
          <div id="venue-drop-down" className={styles.VenueDropDownContainer} data-testid="venue-drop-down-container">
            <a href={getConnectUrl(venueId)} aria-label="doshii-connect" className={styles.logo}>
              <Logo />
            </a>
            {isLoading || currentUser?.venues?.length ? (
              <SelectMenu
                options={(() => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const response =
                    currentUser && currentUser.venues
                      ? currentUser.venues.map(
                          (venue): Option => ({
                            content: <span>{venue.name}</span>,
                            url: `/venue/${venue.hashedId}`,
                            value: venue.hashedId,
                            type: 'link',
                            openNewPage: false
                          })
                        )
                      : [];
                  if (isLoading)
                    response.push({
                      content: (
                        <span className="LoadingCoverText">
                          <i className="mm-plus" />
                          <span>Add venue</span>
                        </span>
                      ),
                      value: 'add-venue',
                      type: 'text'
                    });

                  return response;
                })()}
                value={venueId}
                buttonStyle={styles.HeaderButton}
                align="left"
                maxHeight={VENUE_DROPDOWN_MENU_MAX_HEIGHT}
              />
            ) : null}
          </div>
          <div id="middle-buttons" className={styles.MiddleButtons}>
            {menuItems.map((item: NavMenuItem) => {
              const { url, label, miniLabel, isActive, mobileOnly } = item;

              if (mobileOnly) return null;

              return (
                <Link
                  href={url}
                  onClick={async () => {
                    logAmplitudeEvent({
                      name: `Click ${label.toLocaleLowerCase()}`,
                      category: AmplitudeEventCategories.NAVIGATION,
                      properties: { venueId: venueId || null, source: 'Menu Manager' }
                    });
                    flushAmplitude(); // Flush the event queue to ensure it is sent before the navigation
                  }}
                  key={label}
                  className={`${styles.HeaderButton} ${isActive ? styles.Active : ''}`}
                >
                  {label}
                  {miniLabel ? <span className={styles.MiniLabel}>{miniLabel}</span> : null}
                </Link>
              );
            })}
          </div>
          <div data-testid="settings-container" className={styles.SettingsContainer}>
            <SelectMenu
              options={settings}
              defaultButtonContent={
                currentUser && !isLoading ? (
                  <>
                    <span data-testid="user-name">{currentUser?.givenName}</span>
                    <Icon user={currentUser} inverted={_headerCustomTheme} />
                  </>
                ) : (
                  <>
                    <span className="LoadingCoverText" data-testid="user-name">
                      XXXX XXXXXX
                    </span>
                    <Icon inverted={_headerCustomTheme} />
                  </>
                )
              }
              buttonStyle={styles.HeaderButton}
              lineBeforeLast
              align="right"
              size="small"
            />
          </div>
        </div>
      </div>
      <StickyHeader />
    </nav>
  );
};
